import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Filter, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const fmtRs = (v) => {
  const n = Number(v || 0);
  if (n < 0) return `Rs. -${Math.abs(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `Rs. ${n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const getMonthRange = () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-01`, end: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(new Date(n.getFullYear(), n.getMonth()+1, 0).getDate()).padStart(2,'0')}` }; };

const CashFlow = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const mr = getMonthRange();
  const [startDate, setStartDate] = useState(mr.start);
  const [endDate, setEndDate] = useState(mr.end);

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await api.get(`/accounts/cash-flow?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${BACKEND_URL}/api/accounts/cash-flow/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${BACKEND_URL}/api/accounts/cash-flow/export-csv?${params}`, '_blank');
  };

  const Row = ({ label, value, bold, indent, negative }) => (
    <tr className={bold ? 'bg-[#F5F5F5] font-semibold' : 'border-b border-[#e5e7eb]'}>
      <td className={`py-3 px-5 text-[#1a1a2e] ${indent ? 'pl-10' : ''}`}>{label}</td>
      <td className={`py-3 px-5 text-right font-${bold ? 'semibold' : 'normal'} ${negative ? 'text-[#EF4444]' : 'text-[#1a1a2e]'}`}>{fmtRs(value)}</td>
    </tr>
  );

  // Flat field names from API
  const d = data || {};

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="cash-flow-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="cash-flow-title">Cash Flow Statement</h1>
            <div className="flex gap-2">
              <button onClick={exportPdf} className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-sm text-sm hover:opacity-90" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />Export PDF</button>
              <button onClick={exportCsv} className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-sm text-sm hover:bg-[#f3f4f6]" data-testid="export-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
            </div>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="cash-flow-filters">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="start-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="end-date-input" />
            </div>
            <div className="flex items-end">
              <button onClick={fetchData} className="w-full flex items-center justify-center gap-2 bg-[#09090B] text-white px-4 py-2 rounded-sm text-sm" data-testid="apply-filters-btn"><Filter className="w-4 h-4" />Apply</button>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-[#6b7280]">Loading...</div>
          ) : data ? (
            <div className="space-y-6">
              {/* Section A */}
              <div className="bg-white border border-[#e5e7eb] rounded-sm overflow-hidden" data-testid="section-operating">
                <div className="px-5 py-3 bg-[#EFF6FF] border-b border-[#e5e7eb]">
                  <h3 className="text-sm font-semibold text-[#1E40AF]">A. Operating Activities</h3>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <Row label="Cash received from customers" value={d.cash_received_from_customers} indent />
                    <Row label="Cash paid to suppliers" value={d.cash_paid_to_suppliers} indent />
                    <Row label="Cash paid for expenses" value={d.cash_paid_for_expenses} indent />
                    <Row label="Net Cash from Operating Activities" value={d.net_cash_from_operations} bold negative={d.net_cash_from_operations < 0} />
                  </tbody>
                </table>
              </div>

              {/* Section B */}
              <div className="bg-white border border-[#e5e7eb] rounded-sm overflow-hidden" data-testid="section-financing">
                <div className="px-5 py-3 bg-[#EFF6FF] border-b border-[#e5e7eb]">
                  <h3 className="text-sm font-semibold text-[#1E40AF]">B. Financing Activities</h3>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <Row label="Capital introduced" value={d.capital_introduced} indent />
                    <Row label="Owner drawings" value={d.owner_drawings} indent />
                    <Row label="Net Cash from Financing Activities" value={d.net_cash_from_financing} bold negative={d.net_cash_from_financing < 0} />
                  </tbody>
                </table>
              </div>

              {/* Section C */}
              <div className="bg-white border border-[#e5e7eb] rounded-sm overflow-hidden" data-testid="section-summary">
                <div className="px-5 py-3 bg-[#f9fafb] border-b border-[#e5e7eb]">
                  <h3 className="text-sm font-semibold text-[#1a1a2e]">C. Net Cash Movement</h3>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <Row label="Opening Cash + Bank Balance" value={d.opening_cash_bank} indent />
                    <Row label="Plus: Net Cash from Operations" value={d.net_cash_from_operations} indent negative={d.net_cash_from_operations < 0} />
                    <Row label="Plus: Net Cash from Financing" value={d.net_cash_from_financing} indent negative={d.net_cash_from_financing < 0} />
                    <Row label="Closing Cash + Bank Balance" value={d.closing_cash_bank} bold negative={d.closing_cash_bank < 0} />
                  </tbody>
                </table>
              </div>

              {/* Net Cash Movement — large bold closing position */}
              <div className={`border rounded-sm p-6 ${d.cash_increased ? 'bg-[#DBEAFE] border-[#93C5FD]' : 'bg-[#FEF2F2] border-[#FCA5A5]'}`} data-testid="cash-indicator">
                <div className="flex items-center gap-2 mb-3">
                  {d.cash_increased
                    ? <><ArrowUpRight className="w-5 h-5 text-[#1E40AF]" /><span className="text-sm font-semibold text-[#1E40AF]">Cash Position Increased</span></>
                    : <><ArrowDownRight className="w-5 h-5 text-[#EF4444]" /><span className="text-sm font-semibold text-[#EF4444]">Cash Position Decreased</span></>
                  }
                </div>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Net Cash Movement</p>
                    <p className={`text-3xl font-bold ${(d.closing_cash_bank - d.opening_cash_bank) >= 0 ? 'text-[#1E40AF]' : 'text-[#EF4444]'}`}>
                      {fmtRs(d.closing_cash_bank - d.opening_cash_bank)}
                    </p>
                  </div>
                  <div className="text-sm text-[#6b7280] space-y-1">
                    <p>Opening Balance: <span className="font-medium text-[#1a1a2e]">{fmtRs(d.opening_cash_bank)}</span></p>
                    <p>Closing Balance: <span className={`font-medium ${d.closing_cash_bank >= 0 ? 'text-[#1a1a2e]' : 'text-[#EF4444]'}`}>{fmtRs(d.closing_cash_bank)}</span></p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default CashFlow;
